import React, { useEffect, useState } from 'react';
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  BarsArrowDownIcon,
  ChevronDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import ReactTooltip from 'react-tooltip';
import { getInventoryLeftColor } from '../../components/InfoItem';
import { ACTIVE, enumInventoryPhasedStatus, IN_ACTIVE, PHASED_STATUSES, SUNSET, PLANNED } from '../../consts/inventoryStatus';
import { ALL, enumStockable, LESS_THEN_180, LOW_STOCK, STOCKABLE_VALUES_ARRAY } from '../../consts/inventoryFilter';
import { BOOLEAN_FILTERS, enumCommonBooleanStatuses } from '../../consts/common';
import { getYearsFromToCurrent, getCronStatusColor } from '../../helpers/common';
import { formatDateByGMT } from '../../helpers/formatDateByGMT';
import { CRON_COMPLETED, enumCronStatuses } from '../../consts/cronTypes';
import { format } from 'date-fns';
import { SORT_DIRECTION } from '../../consts/general';

const getStatusPillColor = (status) => {
  switch (status) {
    case ACTIVE:
      return 'bg-primary text-white';
    case IN_ACTIVE:
      return 'bg-red-100 text-red-800';
    case SUNSET:
      return 'bg-yellow-100 text-yellow-800';
    case PLANNED:
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-green-100 text-green-800';
  }
};

const SORT_OPTIONS = [
  { label: 'Product line (default)', value: 'product_line' },
  { label: 'On hand', value: 'on_hand' },
  { label: 'On order', value: 'on_order' },
  { label: 'Days of stock', value: 'warehouse_days_of_stock' },
  { label: 'Sales 30D', value: 'sales_last_30' },
  { label: 'Sales 90D', value: 'sales_last_90' },
  { label: 'Sales 365D', value: 'sales_last_365' },
  { label: 'Daily average', value: 'sales_daily_avg' },
  { label: 'Total days', value: 'days_of_stock' },
  { label: 'Sellout date', value: 'sell_out_date' },
  { label: 'Reorder deadline', value: 'reorder_deadline' },
];

const SheetOverlay = ({ onClose, title, children, footer }) => {
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 max-h-[85vh] bg-white rounded-t-2xl flex flex-col">
        <div className="flex justify-center pt-3">
          <div className="h-1.5 w-12 rounded-full bg-gray-300" />
        </div>
        <div className="relative px-4 py-3 text-center">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4">{children}</div>
        {footer && <div className="border-t border-gray-200 p-3">{footer}</div>}
      </div>
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mt-5 mb-2">{children}</div>
);

const CheckRow = ({ label, checked, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full flex items-center justify-between py-2.5 text-left"
  >
    <span className="text-sm text-gray-900">{label}</span>
    <span
      className={`h-5 w-5 rounded flex items-center justify-center border ${
        checked ? 'bg-gray-900 border-gray-900 text-white' : 'border-gray-300 bg-white'
      }`}
    >
      {checked && (
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
          <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 111.4-1.4l2.8 2.8 6.8-6.8a1 1 0 011.4 0z" clipRule="evenodd" />
        </svg>
      )}
    </span>
  </button>
);

const toggleInArray = (arr, value) =>
  arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

const StatBlock = ({ label, children }) => (
  <div className="min-w-0">
    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</div>
    <div className="mt-0.5 text-sm font-semibold text-gray-900">{children}</div>
  </div>
);

const DetailBlock = ({ label, children }) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</span>
    <span className="text-sm text-gray-700">{children}</span>
  </div>
);

export const MobilePaginationBar = ({
  currentItemsPerPage,
  setCurrentItemsPerPage,
  currentPage,
  setCurrentPage,
  rangeRows,
  isTheLastPage,
}) => (
  <div className="mt-4 flex flex-col items-center justify-between gap-3 md:hidden">
    <div className="flex items-center gap-2 text-sm text-gray-500">
      <span>Rows:</span>
      <div className="flex gap-1">
        {[10, 25, 50, 100].map((size) => (
          <button
            key={size}
            type="button"
            onClick={() => {
              setCurrentPage(1);
              setCurrentItemsPerPage(size);
            }}
            className={`min-h-9 rounded px-2.5 text-sm transition-colors ${
              Number(currentItemsPerPage) === size
                ? 'bg-gray-900 font-semibold text-white'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            {size}
          </button>
        ))}
      </div>
    </div>
    <div className="flex items-center gap-3">
      <button
        type="button"
        aria-label="Previous page"
        disabled={currentPage === 1}
        onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
        className="flex min-h-11 min-w-11 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm disabled:opacity-40"
      >
        <ChevronLeftIcon className="h-4 w-4" />
      </button>
      {rangeRows && <span className="text-sm text-gray-500">{rangeRows}</span>}
      <button
        type="button"
        aria-label="Next page"
        disabled={isTheLastPage}
        onClick={() => !isTheLastPage && setCurrentPage(currentPage + 1)}
        className="flex min-h-11 min-w-11 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm disabled:opacity-40"
      >
        <ChevronRightIcon className="h-4 w-4" />
      </button>
    </div>
  </div>
);

const StockLevelsMobile = ({
  products,
  totalLabel,
  searchTerm,
  setSearchTerm,
  activeFilter,
  setActiveFilter,
  selectedPhasedStatuses,
  setSelectedPhasedStatuses,
  selectedCategories,
  setSelectedCategories,
  catalogCategoryOptionIds,
  catalogCategoryNameById,
  selectedIsNew,
  setSelectedIsNew,
  selectedIsReplenishable,
  setSelectedIsReplenishable,
  selectedStockable,
  setSelectedStockable,
  demandBasisType,
  setDemandBasisType,
  trailingPeriod,
  setTrailingPeriod,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  setCustomRangeApplied,
  quarter,
  setQuarter,
  year,
  setYear,
  growthMultiplier,
  setGrowthMultiplier,
  setSelectedPreset,
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
  setCurrentPage,
  editedExtraOrders,
  setEditedExtraOrders,
  calculateGrowthAdjustedValues,
  calculateRelatedByAdditionalOrder,
  getCategoryAndColor,
  cronStatus,
  cronUpdatedDate,
  cronError,
  cronLoading,
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [showSort, setShowSort] = useState(false);

  const demandLabel = (() => {
    if (demandBasisType === 'trailing') return `Trailing ${trailingPeriod}`;
    if (demandBasisType === 'custom') {
      return customStartDate && customEndDate ? `${customStartDate} → ${customEndDate}` : 'Custom Range';
    }
    if (demandBasisType === 'quarter') return `${quarter} ${year} (YoY)`;
    return 'Current Demand';
  })();

  const setGrowth = (value) => {
    setGrowthMultiplier(value);
    if ([0, 10, 25, 50].includes(value)) {
      setSelectedPreset(String(value));
    } else {
      setSelectedPreset('');
    }
  };

  const resetFilters = () => {
    setActiveFilter(ALL);
    setSelectedPhasedStatuses([ACTIVE, SUNSET, PLANNED]);
    setSelectedCategories([]);
    setSelectedIsNew([...BOOLEAN_FILTERS]);
    setSelectedIsReplenishable([...BOOLEAN_FILTERS]);
    setSelectedStockable([...STOCKABLE_VALUES_ARRAY]);
    setDemandBasisType('trailing');
    setTrailingPeriod('90D');
    setCustomStartDate('');
    setCustomEndDate('');
    setCustomRangeApplied(false);
    setGrowth(0);
    setCurrentPage(1);
  };

  const onSortSelect = (value) => {
    setCurrentPage(1);
    if (sortBy === value) {
      setSortDirection(sortDirection === SORT_DIRECTION.ASCENDING ? SORT_DIRECTION.DESCENDING : SORT_DIRECTION.ASCENDING);
    } else {
      setSortBy(value);
      setSortDirection(SORT_DIRECTION.ASCENDING);
    }
  };

  const onExtraOrderedChange = (product, inputValue) => {
    if (inputValue === '') {
      setEditedExtraOrders({
        ...editedExtraOrders,
        [product.id]: { additionalOrder: 0 },
      });
      return;
    }
    const numericValue = parseInt(inputValue, 10);
    if (isNaN(numericValue) || numericValue < 0) return;
    setEditedExtraOrders({
      ...editedExtraOrders,
      [product.id]: { additionalOrder: numericValue },
    });
  };

  const maxCurrentYearEndDate = `${new Date().getFullYear()}-12-31`;

  const renderCard = (product) => {
    const growthAdjusted = calculateGrowthAdjustedValues(product);
    const related = calculateRelatedByAdditionalOrder(product);

    const warehouseDays = growthAdjusted?.warehouseDaysOfStock ?? product.warehouseDaysOfStock;
    const warehouseSellOut = growthAdjusted?.warehouseSellOutDate ?? product.warehouseSellOutDays;
    const daysOfStock = growthAdjusted?.daysOfStock ?? (related?.daysOfStock ?? product.daysOfStock);
    const sellOutDate = growthAdjusted?.sellOutDate ??
      (related?.sellOutDate ??
        (product.sellOutDate ? format(new Date(product.sellOutDate), 'MMM d, yyyy') : null));
    const reorderDeadline = growthAdjusted?.reorderDeadline ?? (related?.reorderDeadline ?? product.reorderDeadline);
    const sellOut180Days = growthAdjusted?.sellOut180Days ?? (related?.sellOut180Days ?? product.sellOut180Days);
    const dailyAvg = growthAdjusted?.adjustedSalesDailyAVG || product.salesDailyAVG;
    const isReorderAsap = !reorderDeadline;
    const isExpanded = !!expandedRows[product.id];
    const categoryAndColor = getCategoryAndColor(product);
    const isSoldOut = warehouseSellOut === 'Sold Out';
    const isNoVelocity = warehouseSellOut === 'No Velocity';
    const hasAlert = isSoldOut || (isReorderAsap && !isNoVelocity);

    return (
      <div
        key={product.sku}
        className={`border-b border-gray-100 bg-white border-l-2 ${hasAlert ? 'border-l-red-500' : 'border-l-transparent'}`}
      >
        <button
          type="button"
          aria-expanded={isExpanded}
          onClick={() => setExpandedRows((prev) => ({ ...prev, [product.id]: !prev[product.id] }))}
          className="w-full px-3 py-3 text-left active:bg-gray-50"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="truncate text-[15px] font-bold leading-snug text-gray-900" title={product.productLine || 'N/A'}>
                {product.productLine || 'N/A'}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
                <span className="font-medium">{product.sku}</span>
                {categoryAndColor ? (
                  <>
                    <span className="text-gray-300">·</span>
                    <span className="truncate">{categoryAndColor}</span>
                  </>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full shrink-0 ${getInventoryLeftColor(warehouseDays)}`}>
                {Math.round(warehouseDays || 0).toLocaleString('en-US')}
              </span>
              <div className="flex items-center gap-1.5">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full shrink-0 ${getStatusPillColor(product.status)}`}>
                  {product.status ? enumInventoryPhasedStatus[product.status] : enumInventoryPhasedStatus[ACTIVE]}
                </span>
                {isSoldOut ? (
                  <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800">
                    Sold Out
                  </span>
                ) : isNoVelocity ? (
                  <span className="inline-flex rounded-full bg-gray-100 px-2 text-xs font-medium leading-5 text-gray-500">
                    No velocity
                  </span>
                ) : isReorderAsap ? (
                  <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800">
                    Reorder ASAP
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="mt-2.5 flex items-end justify-between gap-2">
            <div className="grid flex-1 grid-cols-4 gap-2">
              <StatBlock label="On Hand">{Number(product.onHand || 0).toLocaleString('en-US')}</StatBlock>
              <StatBlock label="On Order">{Number(product.onOrder || 0).toLocaleString('en-US')}</StatBlock>
              <StatBlock label="Daily Avg">{dailyAvg || 0}</StatBlock>
              <StatBlock label="Sellout">
                <span
                  className={`whitespace-nowrap text-xs font-semibold leading-5 ${
                    isSoldOut ? 'text-red-600' : isNoVelocity ? 'text-gray-400' : ''
                  }`}
                >
                  {warehouseSellOut || '—'}
                </span>
              </StatBlock>
            </div>
            <ChevronDownIcon
              className={`mb-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            />
          </div>
        </button>
        {isExpanded && (
          <div className="border-t border-gray-100 bg-gray-50/60 px-3 py-3">
            <div className="grid grid-cols-3 gap-x-3 gap-y-3">
              <DetailBlock label="Sales 30D">{growthAdjusted?.adjustedSalesLast30 ?? product.salesLast30}</DetailBlock>
              <DetailBlock label="Sales 90D">{growthAdjusted?.adjustedSalesLast90 ?? product.salesLast90}</DetailBlock>
              <DetailBlock label="Sales 365D">{growthAdjusted?.adjustedSalesLast365 ?? product.salesLast365}</DetailBlock>
              <DetailBlock label="Total Days">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getInventoryLeftColor(daysOfStock)}`}>
                  {Math.round(daysOfStock || 0).toLocaleString('en-US')}
                </span>
              </DetailBlock>
              <DetailBlock label="Sellout (Total)">{sellOutDate || '—'}</DetailBlock>
              <DetailBlock label="Reorder By">
                {reorderDeadline || <span className="font-semibold text-red-600">ASAP</span>}
              </DetailBlock>
              <DetailBlock label="Reorder Qty">{product.reorderQuantity || '0'}</DetailBlock>
              <DetailBlock label="Stock Arrives">
                <span className={product.arrivesOnValue ? '' : 'text-gray-400'}>
                  {product.arrivesOnValue || 'Nothing On Order'}
                </span>
              </DetailBlock>
              <DetailBlock label="180D Sellout">
                {sellOut180Days || sellOut180Days === null ? (
                  <XCircleIcon className="h-5 w-5 text-red-500" />
                ) : (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                )}
              </DetailBlock>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5">
              <label htmlFor={`extra-${product.sku}`} className="text-xs font-medium uppercase tracking-wide text-gray-500">Extra Ordered</label>
              <input
                id={`extra-${product.sku}`}
                type="number"
                min="0"
                inputMode="numeric"
                value={
                  editedExtraOrders[product.id]?.additionalOrder !== undefined
                    ? editedExtraOrders[product.id]?.additionalOrder
                    : 0
                }
                onKeyDown={(event) => {
                  if (event.key === '-' || event.key === 'e' || event.key === 'E' || event.key === '+') {
                    event.preventDefault();
                  }
                  const currentValue = editedExtraOrders[product.id]?.additionalOrder ?? 0;
                  if (currentValue === 0 && /[0-9]/.test(event.key)) {
                    event.target.select();
                  }
                }}
                onFocus={(e) => {
                  const currentValue = editedExtraOrders[product.id]?.additionalOrder ?? 0;
                  if (currentValue === 0) {
                    e.target.select();
                  }
                }}
                onChange={(e) => onExtraOrderedChange(product, e.target.value)}
                className="h-10 w-24 rounded-md border border-gray-300 px-2 text-center text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const quickViewButton = (label, filterValue, Icon) => (
    <button
      type="button"
      onClick={() => setActiveFilter(filterValue)}
      className={`w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-left ${
        activeFilter === filterValue ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'
      }`}
    >
      {Icon && <Icon className="h-4 w-4" />}
      {label}
    </button>
  );

  const demandTypeButton = (label, value) => (
    <button
      type="button"
      onClick={() => setDemandBasisType(value)}
      className={`px-3 py-2 rounded-lg text-sm font-medium ${
        demandBasisType === value ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'
      }`}
    >
      {label}
    </button>
  );

  const isCronOk = cronStatus === CRON_COMPLETED;

  return (
    <div className="md:hidden">
      <div className="sticky top-0 z-30 -mx-4 sm:-mx-6 border-b border-gray-200 bg-white px-4 sm:px-6 pb-2 pt-2">
        <div className="flex items-center gap-2">
          <h1 className="flex-1 truncate text-lg font-bold text-gray-900">Stock Levels</h1>
          {!cronLoading && (
            <>
              {isCronOk ? (
                <span className="flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  cron ok
                </span>
              ) : (
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${getCronStatusColor(cronStatus)}`}>
                  {cronStatus ? enumCronStatuses[cronStatus] : 'N/A'}
                </span>
              )}
              <InformationCircleIcon
                className="h-4 w-4 text-gray-400"
                data-tip={true}
                data-for="mobile_cron_tooltip"
              />
              <ReactTooltip id="mobile_cron_tooltip" html={true} effect="solid" place="left">
                {`
                  <div>
                    <div>Updated: ${cronUpdatedDate ? formatDateByGMT(cronUpdatedDate) : 'N/A'}</div>
                    ${cronError ? `<div>Error: ${cronError}</div>` : ''}
                  </div>
                `}
              </ReactTooltip>
            </>
          )}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-11 w-full pl-10 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
          <button
            type="button"
            aria-label="Filters"
            onClick={() => setShowFilters(true)}
            className="h-11 w-11 flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 shadow-sm"
          >
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Sort"
            onClick={() => setShowSort(true)}
            className="h-11 w-11 flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 shadow-sm"
          >
            <BarsArrowDownIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between py-2 text-xs text-gray-500">
        <span>{totalLabel}</span>
        <span>Demand: {demandLabel}</span>
      </div>

      <div className="-mx-4 sm:-mx-6 border-t border-gray-200 bg-white">
        {products.map((product) => renderCard(product))}
      </div>

      {showFilters && (
        <SheetOverlay
          onClose={() => setShowFilters(false)}
          title="Filters"
          footer={
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => {
                  if (demandBasisType === 'custom' && customStartDate && customEndDate && customEndDate >= customStartDate) {
                    setCustomRangeApplied(true);
                  }
                  setShowFilters(false);
                }}
                className="flex-1 px-4 py-2.5 rounded-lg bg-gray-900 text-sm font-medium text-white"
              >
                Show results
              </button>
            </div>
          }
        >
          <SectionLabel>Quick view</SectionLabel>
          <div className="flex flex-col gap-2">
            {quickViewButton('All products', ALL)}
            {quickViewButton('Low stock alerts', LOW_STOCK, ExclamationTriangleIcon)}
            {quickViewButton('<180d inventory', LESS_THEN_180, CalendarDaysIcon)}
          </div>

          <SectionLabel>Status</SectionLabel>
          <div className="divide-y divide-gray-100">
            {PHASED_STATUSES.filter((status) => status !== IN_ACTIVE).map((status) => (
              <CheckRow
                key={status}
                label={enumInventoryPhasedStatus[status]}
                checked={selectedPhasedStatuses.includes(status)}
                onToggle={() => setSelectedPhasedStatuses(toggleInArray(selectedPhasedStatuses, status))}
              />
            ))}
          </div>

          <SectionLabel>Categories</SectionLabel>
          <div className="divide-y divide-gray-100">
            {catalogCategoryOptionIds.map((categoryId) => (
              <CheckRow
                key={categoryId}
                label={catalogCategoryNameById[categoryId] || categoryId}
                checked={selectedCategories.includes(categoryId)}
                onToggle={() => setSelectedCategories(toggleInArray(selectedCategories, categoryId))}
              />
            ))}
          </div>

          <SectionLabel>Is New</SectionLabel>
          <div className="divide-y divide-gray-100">
            {BOOLEAN_FILTERS.map((value) => (
              <CheckRow
                key={`isnew_${value}`}
                label={enumCommonBooleanStatuses[value]}
                checked={selectedIsNew.includes(value)}
                onToggle={() => setSelectedIsNew(toggleInArray(selectedIsNew, value))}
              />
            ))}
          </div>

          <SectionLabel>Replenishable</SectionLabel>
          <div className="divide-y divide-gray-100">
            {BOOLEAN_FILTERS.map((value) => (
              <CheckRow
                key={`repl_${value}`}
                label={enumCommonBooleanStatuses[value]}
                checked={selectedIsReplenishable.includes(value)}
                onToggle={() => setSelectedIsReplenishable(toggleInArray(selectedIsReplenishable, value))}
              />
            ))}
          </div>

          <SectionLabel>Stockable</SectionLabel>
          <div className="divide-y divide-gray-100">
            {STOCKABLE_VALUES_ARRAY.map((value) => (
              <CheckRow
                key={`stockable_${value}`}
                label={enumStockable[value]}
                checked={selectedStockable.includes(value)}
                onToggle={() => setSelectedStockable(toggleInArray(selectedStockable, value))}
              />
            ))}
          </div>

          <SectionLabel>Demand basis</SectionLabel>
          <div className="grid grid-cols-2 gap-2">
            {demandTypeButton('Trailing Range', 'trailing')}
            {demandTypeButton('Custom Range', 'custom')}
            {demandTypeButton('YoY Quarter', 'quarter')}
            {demandTypeButton('Current Demand', 'current-demand')}
          </div>
          {demandBasisType === 'trailing' && (
            <div className="mt-3">
              <div className="flex gap-2">
                {['30D', '90D', '365D'].map((period) => (
                  <button
                    key={period}
                    type="button"
                    onClick={() => setTrailingPeriod(period)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                      trailingPeriod === period ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    Last {period}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">Uses trailing sales window to compute Daily Avg + Sellout.</p>
            </div>
          )}
          {demandBasisType === 'custom' && (
            <div className="mt-3 flex flex-col gap-2">
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    min="2023-01-01"
                    max={maxCurrentYearEndDate}
                    onChange={(e) => {
                      const newStartDate = e.target.value;
                      setCustomStartDate(newStartDate);
                      if (customEndDate && newStartDate && customEndDate < newStartDate) {
                        setCustomEndDate('');
                      }
                    }}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    min={customStartDate || undefined}
                    max={maxCurrentYearEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              {customStartDate && customEndDate && customEndDate < customStartDate && (
                <p className="text-xs text-red-600">End date cannot be before start date.</p>
              )}
              <p className="text-xs text-gray-500">Use any period (e.g., Q1 only) to avoid Q4 inflation.</p>
            </div>
          )}
          {demandBasisType === 'quarter' && (
            <div className="mt-3 flex gap-2">
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-700">Quarter</label>
                <select
                  value={quarter}
                  onChange={(e) => setQuarter(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-700">Year</label>
                <select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  {getYearsFromToCurrent(2024).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <SectionLabel>Growth multiplier</SectionLabel>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Decrease growth multiplier"
              onClick={() => setGrowth(Math.max(0, growthMultiplier - 5))}
              className="h-10 w-10 rounded-lg border border-gray-300 bg-white flex items-center justify-center"
            >
              <MinusIcon className="h-5 w-5 text-gray-700" />
            </button>
            <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white flex-1">
              <input
                type="number"
                min="0"
                value={growthMultiplier}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  if (inputValue === '') {
                    setGrowth(0);
                    return;
                  }
                  const numericValue = parseInt(inputValue, 10);
                  if (isNaN(numericValue) || numericValue < 0) return;
                  setGrowth(numericValue);
                }}
                onKeyDown={(e) => {
                  if (e.key === '-' || e.key === 'e' || e.key === '+' || e.key === 'E') {
                    e.preventDefault();
                  }
                }}
                className="flex-1 text-center text-sm font-medium focus:outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
              />
              <span className="text-sm font-medium text-gray-500">%</span>
            </div>
            <button
              type="button"
              aria-label="Increase growth multiplier"
              onClick={() => setGrowth(growthMultiplier + 5)}
              className="h-10 w-10 rounded-lg border border-gray-300 bg-white flex items-center justify-center"
            >
              <PlusIcon className="h-5 w-5 text-gray-700" />
            </button>
          </div>
          <div className="mt-2 flex gap-2">
            {[0, 10, 25, 50].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setGrowth(preset)}
                className={`flex-1 px-2 py-2 rounded-lg text-sm font-medium ${
                  growthMultiplier === preset ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {preset === 0 ? '0%' : `+${preset}%`}
              </button>
            ))}
          </div>
          <p className="mt-2 mb-1 text-xs text-gray-500">Applies to demand velocity for sellout projection.</p>
        </SheetOverlay>
      )}

      {showSort && (
        <SheetOverlay onClose={() => setShowSort(false)} title="Sort by">
          <div className="flex flex-col">
            {SORT_OPTIONS.map((option) => {
              const isActive = sortBy === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onSortSelect(option.value)}
                  className={`flex items-center justify-between rounded-lg px-3 py-3 text-left text-sm ${
                    isActive ? 'bg-gray-100 font-semibold text-gray-900' : 'text-gray-700'
                  }`}
                >
                  {option.label}
                  {isActive && (
                    sortDirection === SORT_DIRECTION.ASCENDING ? (
                      <ArrowUpIcon className="h-4 w-4 text-gray-600" />
                    ) : (
                      <ArrowDownIcon className="h-4 w-4 text-gray-600" />
                    )
                  )}
                </button>
              );
            })}
          </div>
          <p className="mt-2 mb-1 text-xs text-gray-400">Tap the active field again to flip direction.</p>
        </SheetOverlay>
      )}
    </div>
  );
};

export default StockLevelsMobile;
